/**
 * Displays an ordered list of module actions with edit and delete actions.
 * Empty state prompts users to add their first action.
 */
import type { ModuleActionInfo } from "./ActionConfigDialog";
import { ActionItem } from "./ActionItem";

interface ActionListProps {
	actions: ModuleActionInfo[];
	onEditAction: (action: ModuleActionInfo) => void;
	onDeleteAction: (actionId: string) => void;
	onReorder: (actionOrders: { actionId: string; order: number }[]) => void;
}

/**
 * Displays a list of workflow actions with reorder support.
 * @param props - component props
 * @param props.actions - array of actions to display
 * @param props.onEditAction - callback when an action edit is requested
 * @param props.onDeleteAction - callback when an action delete is requested
 */
export function ActionList({ actions, onEditAction, onDeleteAction }: ActionListProps) {
	if (actions.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-center">
				<p className="text-muted-foreground mb-2">No actions configured yet</p>
				<p className="text-sm text-muted-foreground">
					Add actions to create a workflow for this module.
				</p>
			</div>
		);
	}

	// Sort by order
	const sortedActions = [...actions].sort((a, b) => a.order - b.order);

	return (
		<div className="space-y-2">
			{sortedActions.map((action, index) => (
				<div key={action.id} className="flex items-center gap-2">
					<span className="text-xs text-muted-foreground w-6 text-center">{index + 1}</span>
					<div className="flex-1">
						<ActionItem
							action={action}
							onEdit={() => { onEditAction(action); }}
							onDelete={() => { onDeleteAction(action.id); }}
						/>
					</div>
				</div>
			))}
		</div>
	);
}
